import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import classNames from "classnames";
import L from "leaflet";
import numeral from "numeral";
import { Marker, Polyline, Rectangle, Tooltip, useMap } from "react-leaflet";
import styles from "../styles/components/rectangle.module.scss";

export interface OnDemandPoint {
  activePointId: string;
  gridPoints: [
    {
      id: string;
      cenLat: number;
      cenLon: number;
      bounds?: EditableGridBoundsState;
    }
  ];
}

export type BoundsState = {
  latMin: number;
  latMax: number;
  lonMin: number;
  lonMax: number;
};

export type EditableGridPointState = {
  id: string;
  lat: number;
  lon: number;
};

export type EditableGridBoundsState = {
  outerBounds: BoundsState;
  innerBounds: BoundsState;
  centerLat?: number;
  centerLon?: number;
  centerPoints?: EditableGridPointState[];
};

function disableMapInteraction(map: L.Map) {
  map.dragging.disable();
  map.scrollWheelZoom.disable();
  map.doubleClickZoom.disable();
  map.boxZoom.disable();
  map.keyboard.disable();
  map.touchZoom.disable();
}

function enableMapInteraction(map: L.Map) {
  map.dragging.enable();
  map.scrollWheelZoom.enable();
  map.doubleClickZoom.enable();
  map.boxZoom.enable();
  map.keyboard.enable();
  map.touchZoom.enable();
}

const snap = (value: number, step: number) =>
  +(Math.round(value / step) * step).toFixed(6);

function makeInitialOuterBounds(
  centerLat: number,
  centerLon: number
): BoundsState {
  const mainStep = 1;
  const rows = 6;
  const cols = Math.floor(rows / Math.cos((centerLat * Math.PI) / 180));

  const latMin = snap(centerLat - (rows * mainStep) / 2, mainStep);
  const lonMin = snap(centerLon - (cols * mainStep) / 2, mainStep);

  return {
    latMin,
    latMax: +(latMin + rows * mainStep).toFixed(6),
    lonMin,
    lonMax: +(lonMin + cols * mainStep).toFixed(6),
  };
}

function makeInitialInnerBounds(outerBounds: BoundsState): BoundsState {
  const mainStep = 1;
  const innerSizeRows = 1;
  const innerSizeCols = 2;

  const gridCenterLat = +(
    (outerBounds.latMin + outerBounds.latMax) /
    2
  ).toFixed(6);
  const gridCenterLon = +(
    (outerBounds.lonMin + outerBounds.lonMax) /
    2
  ).toFixed(6);

  return {
    latMin: +(gridCenterLat - (innerSizeRows * mainStep) / 2).toFixed(6),
    latMax: +(gridCenterLat + (innerSizeRows * mainStep) / 2).toFixed(6),
    lonMin: +(gridCenterLon - (innerSizeCols * mainStep) / 2).toFixed(6),
    lonMax: +(gridCenterLon + (innerSizeCols * mainStep) / 2).toFixed(6),
  };
}

function resizeBoundsFromCorner(
  bounds: BoundsState,
  corner: string,
  lat: number,
  lon: number,
  step: number
): BoundsState {
  const next = { ...bounds };

  const snappedLat = snap(lat, step);
  const snappedLon = snap(lon, step);

  if (corner.includes("n")) next.latMax = snappedLat;
  if (corner.includes("s")) next.latMin = snappedLat;
  if (corner.includes("e")) next.lonMax = snappedLon;
  if (corner.includes("w")) next.lonMin = snappedLon;

  if (next.latMin > next.latMax) {
    [next.latMin, next.latMax] = [next.latMax, next.latMin];
  }

  if (next.lonMin > next.lonMax) {
    [next.lonMin, next.lonMax] = [next.lonMax, next.lonMin];
  }

  return next;
}

function ResizeHandle({
  corner,
  position,
  color,
  snapStep,
  onMove,
  suppressNextMapClickRef,
  zIndexOffset = 0,
}: {
  corner: string;
  position: [number, number];
  color: string;
  snapStep: number;
  onMove: (corner: string, lat: number, lon: number) => boolean;
  suppressNextMapClickRef?: React.MutableRefObject<boolean>;
  zIndexOffset?: number;
}) {
  const lastSnappedRef = useRef<string | null>(null);
  const lastValidPositionRef = useRef<[number, number]>(position);

  const markerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    markerRef.current?.setLatLng(position);
    lastValidPositionRef.current = position;
  }, [position]);

  const icon = useMemo(
    () =>
      L.divIcon({
        className: "",
        html: `
      <div style="
        width:14px;
        height:14px;
        border-radius:999px;
        background:${color};
        border:2px solid white;
        box-shadow:0 0 4px black;
        cursor:grab;
      "></div>
    `,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      }),
    [color]
  );

  return (
    <Marker
      ref={(ref) => {
        markerRef.current = ref;
      }}
      zIndexOffset={zIndexOffset}
      position={position}
      draggable
      icon={icon}
      eventHandlers={{
        dragstart: (e) => {
          const map = e.target._map;
          if (map) disableMapInteraction(map);

          lastSnappedRef.current = null;
          lastValidPositionRef.current = position;
        },

        drag: (e) => {
          const marker = e.target;
          const latlng = marker.getLatLng();

          const snappedLat = snap(latlng.lat, snapStep);
          const snappedLon = snap(latlng.lng, snapStep);
          const snappedKey = `${snappedLat},${snappedLon}`;

          if (lastSnappedRef.current === snappedKey) return;

          lastSnappedRef.current = snappedKey;

          const accepted = onMove(corner, snappedLat, snappedLon);

          if (accepted) {
            lastValidPositionRef.current = [snappedLat, snappedLon];
          }
        },

        dragend: (e) => {
          const marker = e.target;
          const map = marker._map;

          suppressNextMapClickRef && (suppressNextMapClickRef.current = true);

          lastSnappedRef.current = null;

          // Force marker back to React-controlled corner position
          requestAnimationFrame(() => {
            marker.setLatLng(position);
          });

          if (map) enableMapInteraction(map);
        },

        click: (e) => {
          L.DomEvent.stopPropagation(e.originalEvent);
        },
      }}
    />
  );
}

function containsBoundsWithBuffer(
  outer: BoundsState,
  inner: BoundsState,
  buffer: number
) {
  return (
    outer.latMin + buffer <= inner.latMin &&
    outer.latMax - buffer >= inner.latMax &&
    outer.lonMin + buffer <= inner.lonMin &&
    outer.lonMax - buffer >= inner.lonMax
  )
}

function containsPointsWithBuffer(
  bounds: BoundsState,
  points: EditableGridPointState[],
  buffer: number
) {
  return points.every((point) => (
    point.lat >= bounds.latMin + buffer &&
    point.lat <= bounds.latMax - buffer &&
    point.lon >= bounds.lonMin + buffer &&
    point.lon <= bounds.lonMax - buffer
  ))
}

function moveBoundsToContainPointsWithBuffer(
  bounds: BoundsState,
  points: EditableGridPointState[],
  buffer: number,
  step: number
): BoundsState {
  let deltaLat = 0;
  let deltaLon = 0;

  points.forEach((point) => {
    const minLat = bounds.latMin + buffer + deltaLat;
    const maxLat = bounds.latMax - buffer + deltaLat;
    const minLon = bounds.lonMin + buffer + deltaLon;
    const maxLon = bounds.lonMax - buffer + deltaLon;

    if (point.lat < minLat) {
      deltaLat += point.lat - minLat;
    } else if (point.lat > maxLat) {
      deltaLat += point.lat - maxLat;
    }

    if (point.lon < minLon) {
      deltaLon += point.lon - minLon;
    } else if (point.lon > maxLon) {
      deltaLon += point.lon - maxLon;
    }
  });

  if (deltaLat === 0 && deltaLon === 0) {
    return bounds;
  }

  return moveBoundsByDelta(bounds, deltaLat, deltaLon, step);
}

function containsBounds(outer: BoundsState, inner: BoundsState) {
  return (
    outer.latMin <= inner.latMin &&
    outer.latMax >= inner.latMax &&
    outer.lonMin <= inner.lonMin &&
    outer.lonMax >= inner.lonMax
  );
}

function AxisMarkers({
  bounds,
  step,
  color,
  inactive = false,
}: {
  bounds: BoundsState;
  step: number;
  color: string;
  inactive?: boolean;
}) {
  const markers: React.ReactNode[] = [];
  const seen = new Set<string>();

  const addMarker = (
    key: string,
    position: [number, number],
    label?: string,
    direction?: "left" | "bottom"
  ) => {
    const pointKey = `${position[0].toFixed(6)},${position[1].toFixed(6)}`;

    if (seen.has(`${key}-${pointKey}`)) return;
    seen.add(`${key}-${pointKey}`);

    markers.push(
      <Marker
        key={`${key}-${pointKey}`}
        position={position}
        icon={L.divIcon({
          className: classNames(styles.markerIcon, {
            [styles.inactiveMarkerIcon]: inactive,
          }),
          html: `
            <div style="
              width:10px;
              height:10px;
              border-radius:50%;
              background:${inactive ? "#9ca3af" : color};
              opacity:${inactive ? 0.4 : 1};
            "></div>
          `,
          iconSize: [10, 10],
          iconAnchor: [5, 5],
        })}
        interactive={false}
      >
        {label && (
          <Tooltip
            permanent
            direction={direction}
            offset={direction === "left" ? [-8, 0] : [0, 8]}
            className={classNames(styles.gridAxisLabel, {
              [styles.inactiveAxisLabel]: inactive,
            })}
          >
            {label}
          </Tooltip>
        )}
      </Marker>
    );
  };

  // Left vertical axis: markers + latitude labels
  for (
    let lat = bounds.latMin;
    lat <= bounds.latMax + 1e-9;
    lat = +(lat + step).toFixed(6)
  ) {
    addMarker(
      "left",
      [lat, bounds.lonMin],
      `${numeral(lat).format("0.0")}°N`,
      "left"
    );

    // Right side: markers only, skip corners to avoid duplicates
    if (lat > bounds.latMin && lat < bounds.latMax) {
      addMarker("right", [lat, bounds.lonMax]);
    }
  }

  // Bottom horizontal axis: markers + longitude labels
  for (
    let lon = bounds.lonMin;
    lon <= bounds.lonMax + 1e-9;
    lon = +(lon + step).toFixed(6)
  ) {
    addMarker(
      "bottom",
      [bounds.latMin, lon],
      `${numeral(lon).format("0.0")}°E`,
      "bottom"
    );

    // Top side: markers only, skip top-left; keep top-right
    if (lon > bounds.lonMin) {
      addMarker("top", [bounds.latMax, lon]);
    }
  }

  return <>{markers}</>;
}

export function ResizableRectangle({
  bounds,
  color,
  snapStep,
  draggable = false,
  onChange,
  onClick,
  suppressNextMapClickRef,
  inactive = false,
  showFineGrid = false,
  fineGridStep,
  showLabels = false,
  labelStep = snapStep,
  majorGridStep,
  majorGridLineWeight = 2,
}: {
  bounds: BoundsState;
  color: string;
  snapStep: number;
  draggable?: boolean;
  onChange: (bounds: BoundsState) => boolean;
  onClick?: (latlng: L.LatLng) => void;
  suppressNextMapClickRef?: React.MutableRefObject<boolean>;
  inactive?: boolean;
  showFineGrid?: boolean;
  fineGridStep?: number;
  showLabels?: boolean;
  labelStep?: number;
  majorGridStep?: number;
  majorGridLineWeight?: number;
}) {
  const map = useMap();
  const displayColor = inactive ? "#9ca3af" : color;
  const isInteractive = draggable && !inactive;

  const lastSnappedBoundsRef = useRef<BoundsState | null>(null);

  const dragRef = useRef<{
    lastLatLng: L.LatLng;
    lastBounds: BoundsState;
    hasMoved: boolean;
  } | null>(null);

  const corners: { id: string; position: [number, number] }[] = [
    {
      id: "nw",
      position: [bounds.latMax, bounds.lonMin],
    },
    {
      id: "ne",
      position: [bounds.latMax, bounds.lonMax],
    },
    {
      id: "se",
      position: [bounds.latMin, bounds.lonMax],
    },
    {
      id: "sw",
      position: [bounds.latMin, bounds.lonMin],
    },
  ];

  const boundsEqual = (a: BoundsState, b: BoundsState) =>
    a.latMin === b.latMin &&
    a.latMax === b.latMax &&
    a.lonMin === b.lonMin &&
    a.lonMax === b.lonMax;

  const handleMapMouseMove = useCallback(
    (e: L.LeafletMouseEvent) => {
      if (!dragRef.current || inactive) return;

      const deltaLat = e.latlng.lat - dragRef.current.lastLatLng.lat;
      const deltaLon = e.latlng.lng - dragRef.current.lastLatLng.lng;

      const hasMoved =
        Math.abs(deltaLat) > 0.00001 || Math.abs(deltaLon) > 0.00001;

      if (!hasMoved) return;

      dragRef.current.hasMoved = true;
      disableMapInteraction(map);

      const nextBounds = moveBoundsByDelta(
        dragRef.current.lastBounds,
        deltaLat,
        deltaLon,
        snapStep
      );

      if (
        lastSnappedBoundsRef.current &&
        boundsEqual(lastSnappedBoundsRef.current, nextBounds)
      ) {
        return;
      }

      const accepted = onChange(nextBounds);

      if (accepted) {
        dragRef.current.lastLatLng = e.latlng;
        dragRef.current.lastBounds = nextBounds;
        lastSnappedBoundsRef.current = nextBounds;
      }
    },
    [inactive, map, onChange, snapStep]
  );

  const finishDrag = useCallback(
    (latlng: L.LatLng) => {
      if (!dragRef.current) return;

      const { hasMoved } = dragRef.current;

      map.off("mousemove", handleMapMouseMove);
      map.off("mouseup", handleMapMouseUp);

      enableMapInteraction(map);

      if (hasMoved) {
        suppressNextMapClickRef && (suppressNextMapClickRef.current = true);
      } else {
        onClick?.(latlng);
      }

      dragRef.current = null;
      lastSnappedBoundsRef.current = null;
    },
    [map, onClick, handleMapMouseMove, suppressNextMapClickRef]
  );

  const handleMapMouseUp = useCallback(
    (e: L.LeafletMouseEvent) => {
      finishDrag(e.latlng);
    },
    [finishDrag]
  );

  useEffect(() => {
    if (!inactive || !dragRef.current) return;

    map.off("mousemove", handleMapMouseMove);
    map.off("mouseup", handleMapMouseUp);
    enableMapInteraction(map);

    dragRef.current = null;
    lastSnappedBoundsRef.current = null;
  }, [inactive, map, handleMapMouseMove, handleMapMouseUp]);

  return (
    <>
      {showFineGrid && fineGridStep && (
        <RectangleGrid
          bounds={bounds}
          step={fineGridStep}
          color={displayColor}
          inactive={inactive}
          majorStep={majorGridStep}
          majorLineWeight={majorGridLineWeight}
        />
      )}
      {!inactive && <AreaSizeLabel bounds={bounds} inactive={inactive} />}
      <Rectangle
        bounds={[
          [bounds.latMin, bounds.lonMin],
          [bounds.latMax, bounds.lonMax],
        ]}
        pathOptions={{
          color: displayColor,
          weight: 2,
          fill: true,
          fillColor: displayColor,
          fillOpacity: inactive ? 0.08 : 0.15,
          opacity: inactive ? 0.45 : 1,
          interactive: isInteractive,
        }}
        eventHandlers={{
          mousedown: (e) => {
            if (!isInteractive) return;

            L.DomEvent.stopPropagation(e.originalEvent);
            L.DomEvent.preventDefault(e.originalEvent);

            dragRef.current = {
              lastLatLng: e.latlng,
              lastBounds: bounds,
              hasMoved: false,
            };

            lastSnappedBoundsRef.current = bounds;

            map.on("mousemove", handleMapMouseMove);
            map.on("mouseup", handleMapMouseUp);
          },
          click: (e) => {
            if (isInteractive) {
              L.DomEvent.stopPropagation(e.originalEvent);
            }
          },
        }}
      />

      {showLabels && !inactive && (
        <AxisMarkers
          bounds={bounds}
          step={labelStep}
          color={displayColor}
        />
      )}

      {isInteractive &&
        corners.map((corner) => (
          <ResizeHandle
            key={corner.id}
            corner={corner.id}
            position={corner.position}
            color={displayColor}
            snapStep={snapStep}
            suppressNextMapClickRef={suppressNextMapClickRef}
            zIndexOffset={inactive ? 0 : 10000}
            onMove={(cornerId, lat, lon) => {
              return onChange(
                resizeBoundsFromCorner(bounds, cornerId, lat, lon, snapStep)
              );
            }}
          />
        ))}
    </>
  );
}

function RectangleGrid({
  bounds,
  step,
  color,
  inactive = false,
  majorStep,
  majorLineWeight = 2,
}: {
  bounds: BoundsState;
  step: number;
  color: string;
  inactive?: boolean;
  majorStep?: number;
  majorLineWeight?: number;
}) {
  const lines = [];

  const isMajorLine = (value: number) => {
    if (!majorStep) return false;
  
    const rounded = Math.round(value / majorStep) * majorStep;
  
    return Math.abs(value - rounded) < 1e-6;
  };

  for (
    let lat = bounds.latMin;
    lat <= bounds.latMax + 1e-9;
    lat = +(lat + step).toFixed(6)
  ) {
    lines.push(
      <RectangleLine
        key={`h-${lat}`}
        positions={[
          [lat, bounds.lonMin],
          [lat, bounds.lonMax],
        ]}
        color={color}
        inactive={inactive}
        weight={isMajorLine(lat) ? majorLineWeight : 1}
      />
    );
  }

  for (
  let lon = bounds.lonMin;
  lon <= bounds.lonMax + 1e-9;
  lon = +(lon + step).toFixed(6)
) {
  lines.push(
    <RectangleLine
      key={`v-${lon}`}
      positions={[
        [bounds.latMin, lon],
        [bounds.latMax, lon],
      ]}
      color={color}
      inactive={inactive}
      weight={isMajorLine(lon) ? majorLineWeight : 1}
    />
  );
}

  return <>{lines}</>;
}

function RectangleLine({
  positions,
  color,
  inactive = false,
  weight = 1,
}: {
  positions: [number, number][];
  color: string;
  inactive?: boolean;
  weight?: number;
}) {
  return (
    <Polyline
      positions={positions}
      pathOptions={{
        color,
        weight,
        opacity: inactive ? 0.35 : 0.9,
        interactive: false,
      }}
    />
  );
}

function moveBoundsToContainPoint(
  bounds: BoundsState,
  lat: number,
  lon: number,
  step: number
): BoundsState {
  let deltaLat = 0;
  let deltaLon = 0;

  if (lat < bounds.latMin) {
    deltaLat = lat - bounds.latMin;
  } else if (lat > bounds.latMax) {
    deltaLat = lat - bounds.latMax;
  }

  if (lon < bounds.lonMin) {
    deltaLon = lon - bounds.lonMin;
  } else if (lon > bounds.lonMax) {
    deltaLon = lon - bounds.lonMax;
  }

  if (deltaLat === 0 && deltaLon === 0) {
    return bounds;
  }

  return moveBoundsByDelta(bounds, deltaLat, deltaLon, step);
}

function moveBoundsByDelta(
  bounds: BoundsState,
  deltaLat: number,
  deltaLon: number,
  step: number
): BoundsState {
  return {
    latMin: snap(bounds.latMin + deltaLat, step),
    latMax: snap(bounds.latMax + deltaLat, step),
    lonMin: snap(bounds.lonMin + deltaLon, step),
    lonMax: snap(bounds.lonMax + deltaLon, step),
  };
}

function calculateBoundsAreaSqm(bounds: BoundsState) {
  const earthRadius = 6378137;

  const latMin = (bounds.latMin * Math.PI) / 180;
  const latMax = (bounds.latMax * Math.PI) / 180;
  const lonMin = (bounds.lonMin * Math.PI) / 180;
  const lonMax = (bounds.lonMax * Math.PI) / 180;

  return Math.abs(
    earthRadius *
      earthRadius *
      (lonMax - lonMin) *
      (Math.sin(latMax) - Math.sin(latMin))
  );
}

function AreaSizeLabel({
  bounds,
  inactive = false,
}: {
  bounds: BoundsState;
  inactive?: boolean;
}) {
  const areaSqm = calculateBoundsAreaSqm(bounds) / 1_000_000;
  const centerLon = (bounds.lonMin + bounds.lonMax) / 2;
  const labelLat = bounds.latMax;

  return (
    <Marker
      position={[labelLat, centerLon]}
      interactive={false}
      zIndexOffset={inactive ? 0 : 10000}
      icon={L.divIcon({
        className: "",
        html: `
          <div class="${classNames(styles.areaSizeLabel, {
            [styles.inactiveAreaSizeLabel]: inactive,
          })}">
            ${numeral(areaSqm).format("0,0")} km²
          </div>
        `,
        iconSize: [120, 24],
        iconAnchor: [60, 34],
      })}
    />
  );
}

export function EditableGridOverlay({
  centerLat,
  centerLon,
  suppressNextMapClickRef,
  bounds,
  initialBounds,
  onBoundsUpdate,
  inactive = false,
  allowMultiplePoints = true,
  addPointOnClick = true,
}: {
  allowMultiplePoints?: boolean;
  centerLat: number;
  centerLon: number;
  suppressNextMapClickRef?: React.MutableRefObject<boolean>;
  bounds?: EditableGridBoundsState;
  initialBounds?: EditableGridBoundsState;
  addPointOnClick?: boolean;
  onBoundsUpdate?: (
    outerBounds: BoundsState,
    innerBounds: BoundsState,
    centerLat: number,
    centerLon: number,
    centerPoints?: EditableGridPointState[]
  ) => void;
  inactive?: boolean;
}) {
  const defaultOuterBounds = useMemo(
    () => makeInitialOuterBounds(centerLat, centerLon),
    [centerLat, centerLon]
  );

  function containsPoint(bounds: BoundsState, lat: number, lon: number) {
    return (
      lat >= bounds.latMin &&
      lat <= bounds.latMax &&
      lon >= bounds.lonMin &&
      lon <= bounds.lonMax
    );
  }

  const defaultInnerBounds = useMemo(
    () => makeInitialInnerBounds(defaultOuterBounds),
    [defaultOuterBounds]
  );

  const defaultBounds = useMemo<EditableGridBoundsState>(
    () => ({
      outerBounds: defaultOuterBounds,
      innerBounds: defaultInnerBounds,
    }),
    [defaultOuterBounds, defaultInnerBounds]
  );

  const isControlled = !!bounds?.outerBounds && !!bounds?.innerBounds;
  const [uncontrolledBounds, setUncontrolledBounds] =
    useState<EditableGridBoundsState>(initialBounds ?? defaultBounds);

  useEffect(() => {
    if (isControlled || initialBounds) return;

    setUncontrolledBounds(defaultBounds);
  }, [defaultBounds, initialBounds, isControlled]);

  const currentBounds: EditableGridBoundsState = {
    ...defaultBounds,
    ...uncontrolledBounds,
    ...bounds,
    outerBounds:
      bounds?.outerBounds ??
      uncontrolledBounds.outerBounds ??
      defaultBounds.outerBounds,
    innerBounds:
      bounds?.innerBounds ??
      uncontrolledBounds.innerBounds ??
      defaultBounds.innerBounds,
  };

  const { outerBounds, innerBounds } = currentBounds;

  const currentCenterLat = currentBounds.centerLat ?? centerLat;
  const currentCenterLon = currentBounds.centerLon ?? centerLon;

  const commitBounds = useCallback(
    (nextBounds: EditableGridBoundsState) => {
      if (!isControlled) {
        setUncontrolledBounds(nextBounds);
      }

      onBoundsUpdate?.(
        nextBounds.outerBounds,
        nextBounds.innerBounds,
        nextBounds.centerLat ?? currentCenterLat,
        nextBounds.centerLon ?? currentCenterLon,
        nextBounds.centerPoints
      );
    },
    [isControlled, onBoundsUpdate, currentCenterLat, currentCenterLon]
  );

  const centerPoints = useMemo<EditableGridPointState[]>(() => {
    if (allowMultiplePoints && currentBounds.centerPoints?.length) {
      return currentBounds.centerPoints;
    }
  
    return [
      {
        id: "point-0",
        lat: currentBounds.centerLat ?? centerLat,
        lon: currentBounds.centerLon ?? centerLon,
      },
    ];
  }, [
    allowMultiplePoints,
    currentBounds.centerPoints,
    currentBounds.centerLat,
    currentBounds.centerLon,
    centerLat,
    centerLon,
  ]);

  const updateOuterBounds = useCallback(
    (nextOuter: BoundsState) => {
      if (inactive) return false
  
      if (!containsBoundsWithBuffer(nextOuter, innerBounds, 0.5)) {
        return false
      }
  
      commitBounds({
        ...currentBounds,
        outerBounds: nextOuter,
        innerBounds,
        centerLat: centerPoints[0]?.lat,
        centerLon: centerPoints[0]?.lon,
        centerPoints,
      })
  
      return true
    },
    [commitBounds, inactive, innerBounds, currentBounds, centerPoints]
  )

  const updateInnerBounds = useCallback(
    (nextInner: BoundsState) => {
      if (inactive) return false
  
      if (!containsBoundsWithBuffer(outerBounds, nextInner, 0.5)) {
        return false
      }
  
      if (!containsPointsWithBuffer(nextInner, centerPoints, 0.1)) {
        return false
      }
  
      commitBounds({
        ...currentBounds,
        outerBounds,
        innerBounds: nextInner,
        centerLat: centerPoints[0]?.lat,
        centerLon: centerPoints[0]?.lon,
        centerPoints,
      })
  
      return true
    },
    [commitBounds, inactive, outerBounds, currentBounds, centerPoints]
  )

  const centerIcon = useMemo(
    () =>
      L.divIcon({
        className: "",
        html: `
          <div style="
            width:18px;
            height:18px;
            border-radius:999px;
            background:${inactive ? "#9ca3af" : "#ff0000"};
            border:3px solid white;
            box-shadow:0 0 6px black;
            opacity:${inactive ? 0.55 : 1};
          "></div>
        `,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      }),
    [inactive]
  );
  const updateCenterPoint = useCallback(
    (pointId: string, lat: number, lon: number) => {
      if (inactive) return false;

      // The point itself is raw map precision; do not snap it to the grid.
      const nextLat = +lat.toFixed(6);
      const nextLon = +lon.toFixed(6);

      const nextCenterPoints = centerPoints.map((point) =>
        point.id === pointId
          ? {
              ...point,
              lat: nextLat,
              lon: nextLon,
            }
          : point
      );

      // The green bounds remain aligned to the map grid, but move just enough
      // to keep all raw points inside with the configured buffer.
      const nextInnerBounds = moveBoundsToContainPointsWithBuffer(
        innerBounds,
        nextCenterPoints,
        0.1,
        0.1
      );

      if (!containsBoundsWithBuffer(outerBounds, nextInnerBounds, 0.5)) {
        return false;
      }

      if (!containsPointsWithBuffer(nextInnerBounds, nextCenterPoints, 0.1)) {
        return false;
      }

      const primaryPoint = nextCenterPoints[0];

      commitBounds({
        outerBounds,
        innerBounds: nextInnerBounds,
        centerLat: primaryPoint.lat,
        centerLon: primaryPoint.lon,
        centerPoints: allowMultiplePoints ? nextCenterPoints : undefined,
      });

      return true;
    },
    [
      inactive,
      innerBounds,
      outerBounds,
      centerPoints,
      allowMultiplePoints,
      commitBounds,
    ]
  );

  return (
    <>
      {/* Blue outer rectangle first: always behind */}
      <ResizableRectangle
        suppressNextMapClickRef={suppressNextMapClickRef}
        bounds={outerBounds}
        color="#0066ff"
        snapStep={0.5}
        showFineGrid
        fineGridStep={0.1}
        majorGridStep={1}
        majorGridLineWeight={2}
        showLabels
        labelStep={1}
        draggable
        onChange={updateOuterBounds}
        inactive={inactive}
      />


      {/* Green inner rectangle second: always on top and draggable */}
      <ResizableRectangle
        suppressNextMapClickRef={suppressNextMapClickRef}
        bounds={innerBounds}
        color="#00ff00"
        snapStep={0.1}
        draggable
        showFineGrid
        fineGridStep={1/40}
        onChange={updateInnerBounds}
        inactive={inactive}
        onClick={(latlng) => {
          if (!addPointOnClick || !allowMultiplePoints || inactive) return;

          const nextLat = +latlng.lat.toFixed(6);
          const nextLon = +latlng.lng.toFixed(6);

          const nextCenterPoints = [
            ...centerPoints,
            {
              id: `point-${Date.now()}`,
              lat: nextLat,
              lon: nextLon,
            },
          ];

          const nextInnerBounds = moveBoundsToContainPointsWithBuffer(
            innerBounds,
            nextCenterPoints,
            0.1,
            0.1
          );

          if (!containsBoundsWithBuffer(outerBounds, nextInnerBounds, 0.5)) {
            return;
          }

          if (!containsPointsWithBuffer(nextInnerBounds, nextCenterPoints, 0.1)) {
            return;
          }

          const primaryPoint = nextCenterPoints[0];

          commitBounds({
            outerBounds,
            innerBounds: nextInnerBounds,
            centerLat: primaryPoint.lat,
            centerLon: primaryPoint.lon,
            centerPoints: nextCenterPoints,
          });
        }}
      />

      {/* Centre points last so they sit above both bounds. */}
      {centerPoints.map((point) => (
        <Marker
          key={point.id}
          position={[point.lat, point.lon]}
          icon={centerIcon}
          interactive={!inactive}
          draggable={!inactive}
          zIndexOffset={inactive ? 0 : 10000}
          eventHandlers={{
            dragstart: (e) => {
              const map = e.target._map;
              if (map) disableMapInteraction(map);
            },
            dragend: (e) => {
              const marker = e.target;
              const map = marker._map;
              const latlng = marker.getLatLng();

              const accepted = updateCenterPoint(
                point.id,
                latlng.lat,
                latlng.lng
              );

              if (!accepted) {
                marker.setLatLng([point.lat, point.lon]);
              }

              suppressNextMapClickRef &&
                (suppressNextMapClickRef.current = true);

              if (map) enableMapInteraction(map);
            },
            click: (e) => {
              L.DomEvent.stopPropagation(e.originalEvent);
            },
          }}
        />
      ))}
    </>
  );
}
